import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';
import { useAuth } from '../context/AuthContext';

function UserModal({ user: editingUser, onClose, onSuccess, t }) {
  const isEdit = !!editingUser;
  const [form, setForm] = useState({
    username: editingUser?.username || '',
    email:    editingUser?.email    || '',
    password: '',
    role:     editingUser?.role     || 'admin',
    companyName: editingUser?.companyName || '',
    phone:    editingUser?.phone    || '',
  });
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handle = async (e) => {
    e.preventDefault(); setError('');
    setLoading(true);
    try {
      const payload = { username: form.username, email: form.email, role: form.role, companyName: form.companyName, phone: form.phone };
      if (form.password) payload.password = form.password;
      if (isEdit) {
        await axios.put(`/api/auth/admins/${editingUser._id}`, payload);
      } else {
        if (!form.password) { setError('Password is required'); setLoading(false); return; }
        payload.password = form.password;
        await axios.post('/api/auth/admins', payload);
      }
      onSuccess();
    } catch (err) { setError(err.response?.data?.message || 'Error saving'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <h2 style={{ fontSize: 22 }}>{isEdit ? t('editAdmin') : t('addAdmin')}</h2>
          <button onClick={onClose} style={{ background: 'none', color: 'var(--text-muted)', fontSize: 22, lineHeight: 1 }}>×</button>
        </div>
        <form onSubmit={handle}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">{t('username')}</label>
              <input type="text" required value={form.username} onChange={e => set('username', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input type="email" required value={form.email} onChange={e => set('email', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">{isEdit ? t('newPassword') : t('password')}</label>
            <input type="password" placeholder={isEdit ? '••••••••' : ''} required={!isEdit}
              value={form.password} onChange={e => set('password', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">{t('role')}</label>
            <select value={form.role} onChange={e => set('role', e.target.value)}>
              <option value="admin">{t('roleAdmin')}</option>
              <option value="superadmin">{t('roleSuperadmin')}</option>
              <option value="partner">{t('rolePartner')}</option>
            </select>
          </div>
          {form.role === 'partner' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">{t('companyName')}</label>
                <input type="text" value={form.companyName} onChange={e => set('companyName', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('partnerPhone')}</label>
                <input type="text" value={form.phone} onChange={e => set('phone', e.target.value)} />
              </div>
            </div>
          )}
          {error && <div style={{ background: 'var(--red-bg)', color: 'var(--red)', padding: '10px 14px', borderRadius: 'var(--radius-sm)', fontSize: 13, marginBottom: 14, border: '1px solid rgba(224,92,92,0.2)' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button type="button" onClick={onClose} className="btn-ghost" style={{ flex: 1 }}>{t('cancel')}</button>
            <button type="submit" className="btn-primary" style={{ flex: 2 }} disabled={loading}>{loading ? t('saving') : t('save')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AssignModal({ partner, onClose, onSuccess, t }) {
  const [allRestaurants, setAllRestaurants] = useState([]);
  const [selected,       setSelected]       = useState(new Set());
  const [loading,        setLoading]        = useState(true);
  const [saving,         setSaving]         = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get('/api/restaurants'),
    ]).then(([res]) => {
      const list = Array.isArray(res.data) ? res.data : (res.data.restaurants ?? []);
      setAllRestaurants(list);
      const assigned = list.filter(r => {
        const pid = r.partnerId?._id || r.partnerId;
        return String(pid) === String(partner._id);
      }).map(r => r._id);
      setSelected(new Set(assigned));
    }).finally(() => setLoading(false));
  }, [partner._id]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`/api/auth/admins/${partner._id}/restaurants`, { restaurantIds: [...selected] });
      onSuccess();
    } catch (err) { alert(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 520 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: 22 }}>{t('assignRestaurants')}</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 3 }}>{partner.username}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', color: 'var(--text-muted)', fontSize: 22, lineHeight: 1 }}>×</button>
        </div>
        {loading ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>{t('loading')}</div>
        ) : (
          <div>
            <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', marginBottom: 20 }}>
              {allRestaurants.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No restaurants</div>
              ) : allRestaurants.map(r => (
                <label key={r._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'background 0.12s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--table-hover)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <input type="checkbox" checked={selected.has(r._id)} onChange={() => toggle(r._id)}
                    style={{ width: 16, height: 16, accentColor: 'var(--accent)', flexShrink: 0 }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{r.name}</div>
                    {r.address && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.address}</div>}
                  </div>
                  {r.partnerId && String(r.partnerId?._id || r.partnerId) !== String(partner._id) && (
                    <span style={{ fontSize: 10, color: 'var(--text-dim)', marginLeft: 'auto', flexShrink: 0 }}>assigned</span>
                  )}
                </label>
              ))}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>{selected.size} selected</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onClose} className="btn-ghost" style={{ flex: 1 }}>{t('cancel')}</button>
              <button onClick={save} className="btn-primary" style={{ flex: 2 }} disabled={saving}>{saving ? t('saving') : t('save')}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminManagement() {
  const { t }          = useSettings();
  const { admin: me }  = useAuth();
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null);
  const [assignModal, setAssignModal] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get('/api/auth/admins'); setUsers(res.data); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleDelete = async (u) => {
    if (!window.confirm(t('confirmDeleteAdmin'))) return;
    try { await axios.delete(`/api/auth/admins/${u._id}`); fetchUsers(); }
    catch (err) { alert(err.response?.data?.message || 'Error'); }
  };

  const ROLE_BADGE = {
    superadmin: { label: t('roleSuperadmin'), style: { background: 'var(--accent-glow)', color: 'var(--accent)' } },
    admin:      { label: t('roleAdmin'),      style: { background: 'var(--gray-bg)',     color: 'var(--text-muted)' } },
    partner:    { label: t('rolePartner'),    style: { background: 'rgba(100,160,255,0.15)', color: '#6ab0ff' } },
  };

  const admins   = users.filter(u => u.role !== 'partner');
  const partners = users.filter(u => u.role === 'partner');

  const renderTable = (list, title) => (
    <div style={{ marginBottom: 32 }}>
      <h3 style={{ fontSize: 18, marginBottom: 14, color: 'var(--text-muted)', fontFamily: 'DM Serif Display' }}>{title}</h3>
      <div className="card table-scroll" style={{ padding: 0 }}>
        {list.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>{t('noAdmins')}</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {[t('username'), 'Email', t('role'), t('date'), t('actions')].map(h => (
                  <th key={h} style={{ padding: '13px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map(u => {
                const rb   = ROLE_BADGE[u.role] || ROLE_BADGE.admin;
                const isMe = u._id === me?._id;
                return (
                  <tr key={u._id} style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.12s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--table-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '13px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--accent-glow)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                          {u.username[0].toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: 500, fontSize: 14 }}>{u.username}</div>
                          {u.companyName && <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{u.companyName}</div>}
                          {isMe && <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>({t('signedInAs')})</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '13px 16px', color: 'var(--text-muted)', fontSize: 13 }}>{u.email}</td>
                    <td style={{ padding: '13px 16px' }}>
                      <span className="badge" style={rb.style}>{rb.label}</span>
                    </td>
                    <td style={{ padding: '13px 16px', color: 'var(--text-muted)', fontSize: 13, whiteSpace: 'nowrap' }}>
                      {new Date(u.createdAt).toLocaleDateString('en-GB')}
                    </td>
                    <td style={{ padding: '13px 16px' }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button className="btn-ghost" style={{ fontSize: 12, padding: '6px 11px' }} onClick={() => setModal(u)}>
                          {t('editAdmin')}
                        </button>
                        {u.role === 'partner' && (
                          <button className="btn-ghost" style={{ fontSize: 12, padding: '6px 11px' }} onClick={() => setAssignModal(u)}>
                            {t('assignRestaurants')}
                          </button>
                        )}
                        {!isMe && (
                          <button className="btn-danger" style={{ fontSize: 12 }} onClick={() => handleDelete(u)}>
                            {t('delete')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 style={{ fontSize: 34, marginBottom: 6 }}>{t('adminManagement')}</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>{users.length} users total</p>
        </div>
        <button className="btn-primary" onClick={() => setModal('add')}>+ {t('addAdmin')}</button>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>{t('loading')}</div>
      ) : (
        <>
          {renderTable(admins,   'Admins & Superadmins')}
          {renderTable(partners, 'Partners')}
        </>
      )}

      {modal && (
        <UserModal
          user={modal === 'add' ? null : modal}
          onClose={() => setModal(null)}
          onSuccess={() => { setModal(null); fetchUsers(); }}
          t={t}
        />
      )}

      {assignModal && (
        <AssignModal
          partner={assignModal}
          onClose={() => setAssignModal(null)}
          onSuccess={() => { setAssignModal(null); fetchUsers(); }}
          t={t}
        />
      )}
    </div>
  );
}
